import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import { AttributeType, gl } from '@antv/l7-core';
import { calculateCentroid, fp64LowPart, rgb2arr } from '@antv/l7-utils';
import BaseModel from "../../core/BaseModel";
import { PointExtrudeTriangulation } from "../../core/triangulation";
/* babel-plugin-inline-import '../shaders/extrude/extrude_frag.glsl' */
const pointExtrudeFrag = "\nin vec4 v_color;\nin float v_lightWeight;\nout vec4 outputColor;\n\nlayout(std140) uniform commonUniforms {\n  float u_pickLight;\n  float u_heightfixed;\n  float u_r;\n  float u_linearColor;\n  vec4 u_sourceColor;\n  vec4 u_targetColor;\n  float u_opacitylinear;\n  float u_opacitylinear_dir;\n  float u_lightEnable;\n};\n\n#pragma include \"scene_uniforms\"\n#pragma include \"picking\"\n\nvoid main() {\n\n  outputColor = v_color;\n  // \u5F00\u542F\u900F\u660E\u5EA6\u6E10\u53D8\n  // picking\n  if(u_pickLight > 0.0) {\n    outputColor = filterColorAlpha(outputColor, v_lightWeight);\n  } else {\n    outputColor = filterColor(outputColor);\n  }\n}\n";
/* babel-plugin-inline-import '../shaders/extrude/extrude_vert.glsl' */
const pointExtrudeVert = "#define pi (3.1415926535)\n\nlayout(location = ATTRIBUTE_LOCATION_POSITION) in vec3 a_Position;\nlayout(location = ATTRIBUTE_LOCATION_COLOR) in vec4 a_Color;\nlayout(location = ATTRIBUTE_LOCATION_SIZE) in vec3 a_Size;\nlayout(location = ATTRIBUTE_LOCATION_EXTRUDE) in vec4 a_Extrude;\nlayout(location = ATTRIBUTE_LOCATION_NORMAL) in vec3 a_Normal;\n\nlayout(std140) uniform commonUniforms {\n  float u_pickLight;\n  float u_heightfixed;\n  float u_r;\n  float u_linearColor;\n  vec4 u_sourceColor;\n  vec4 u_targetColor;\n  float u_opacitylinear;\n  float u_opacitylinear_dir;\n  float u_lightEnable;\n};\nout vec4 v_color;\nout float v_lightWeight;\n\n#pragma include \"projection\"\n#pragma include \"light\"\n#pragma include \"picking\"\n\nfloat getYRadian(float x, float z) {\n  if (x > 0.0 && z > 0.0) {\n    return atan(x / z);\n  } else if (x > 0.0 && z <= 0.0) {\n    return atan(-z / x) + pi / 2.0;\n  } else if (x <= 0.0 && z <= 0.0) {\n    return pi + atan(x / z); //atan(x/z) +\n  } else {\n    return atan(z / -x) + pi * 3.0 / 2.0;\n  }\n}\n\nfloat getXRadian(float y, float r) {\n  return atan(y / r);\n}\n\nvoid main() {\n  vec3 size = a_Size * a_Position;\n\n  vec3 offset = size; // \u63A7\u5236\u5706\u67F1\u4F53\u7684\u5927\u5C0F - \u4ECE\u6807\u51C6\u5355\u4F4D\u5706\u67F1\u4F53\u8FDB\u884C\u504F\u79FB\n\n  if (u_heightfixed < 1.0) {\n    // \u5706\u67F1\u4F53\u4E0D\u56FA\u5B9A\u9AD8\u5EA6\n  } else {\n    // \u5706\u67F1\u4F53\u56FA\u5B9A\u9AD8\u5EA6 \uFF08 \u5904\u7406 mapbox \uFF09\n    if (\n      u_CoordinateSystem == COORDINATE_SYSTEM_LNGLAT ||\n      u_CoordinateSystem == COORDINATE_SYSTEM_LNGLAT_OFFSET\n    ) {\n      offset *= 4.0 / pow(2.0, 21.0 - u_Zoom);\n    }\n  }\n\n  vec2 positions = a_Extrude.xy;\n  vec2 positions64Low = a_Extrude.zw;\n  vec4 project_pos = project_position(vec4(positions, 0.0, 1.0), positions64Low);\n\n  // u_r \u63A7\u5236\u5706\u67F1\u7684\u751F\u957F\n  vec4 pos = vec4(project_pos.xy + offset.xy, offset.z * u_r, 1.0);\n\n  // // \u5706\u67F1\u5149\u7167\u6548\u679C\n  float lightWeight = 1.0;\n\n  if (u_lightEnable > 0.0) {\n    // \u53D6\u6D88\u4E09\u5143\u8868\u8FBE\u5F0F\uFF0C\u589E\u5F3A\u5065\u58EE\u6027\n    lightWeight = calc_lighting(pos);\n  }\n\n  v_lightWeight = lightWeight;\n\n  v_color = a_Color;\n\n  // \u8BBE\u7F6E\u5706\u67F1\u7684\u5E95\u8272\n  if (u_linearColor == 1.0) {\n    // \u4F7F\u7528\u6E10\u53D8\u989C\u8272\n    v_color = mix(u_sourceColor, u_targetColor, a_Position.z);\n    v_color.a = v_color.a * opacity;\n  } else {\n    v_color = vec4(a_Color.rgb * lightWeight, a_Color.w * opacity);\n  }\n\n  if (u_opacitylinear > 0.0) {\n    v_color.a *= u_opacitylinear_dir > 0.0 ? 1.0 - a_Position.z : a_Position.z;\n  }\n\n  gl_Position = project_common_position_to_clipspace(pos);\n\n  setPickingColor(a_PickingColor);\n}\n";
export default class ExtrudeModel extends BaseModel {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "raiseCount", 0);
    _defineProperty(this, "raiseRepeat", 0);
  }
  get attributeLocation() {
    return Object.assign(super.attributeLocation, {
      MAX: super.attributeLocation.MAX,
      SIZE: 9,
      EXTRUDE: 10,
      NORMAL: 11
    });
  }
  getCommonUniformsInfo() {
    const {
      animateOption = {
        enable: false,
        speed: 0.01,
        repeat: false
      },
      sourceColor,
      targetColor,
      pickLight = false,
      heightfixed = false,
      opacityLinear = {
        enable: false,
        dir: 'up'
      },
      lightEnable = true
    } = this.layer.getLayerConfig();

    // 转化渐变色
    let useLinearColor = 0; // 默认不生效
    let sourceColorArr = [0, 0, 0, 0];
    let targetColorArr = [0, 0, 0, 0];
    if (sourceColor && targetColor) {
      sourceColorArr = rgb2arr(sourceColor);
      targetColorArr = rgb2arr(targetColor);
      useLinearColor = 1;
    }
    if (this.raiseCount < 1 && this.raiseRepeat > 0) {
      if (animateOption.enable) {
        const {
          speed = 0.01
        } = animateOption;
        this.raiseCount += speed;
        if (this.raiseCount >= 1) {
          if (this.raiseRepeat > 1) {
            this.raiseCount = 0;
            this.raiseRepeat--;
          } else {
            this.raiseCount = 1;
          }
        }
      }
    }
    const commonOptions = {
      // 圆柱体的拾取高亮是否要计算光照
      u_pickLight: Number(pickLight),
      // 圆柱体是否固定高度
      u_heightfixed: Number(heightfixed),
      u_r: animateOption.enable && this.raiseRepeat > 0 ? this.raiseCount : 1.0,
      // 渐变色支持参数
      u_linearColor: useLinearColor,
      u_sourceColor: sourceColorArr,
      u_targetColor: targetColorArr,
      // 透明度渐变
      u_opacitylinear: Number(opacityLinear.enable),
      u_opacitylinear_dir: opacityLinear.dir === 'up' ? 1.0 : 0.0,
      // 光照计算开关
      u_lightEnable: Number(lightEnable)
    };
    const commonBufferInfo = this.getUniformsBufferInfo(commonOptions);
    return commonBufferInfo;
  }
  initModels() {
    var _this = this;
    return _asyncToGenerator(function* () {
      return _this.buildModels();
    })();
  }
  buildModels() {
    var _this2 = this;
    return _asyncToGenerator(function* () {
      const {
        depth = true,
        animateOption: {
          repeat = 1
        }
      } = _this2.layer.getLayerConfig();
      _this2.raiseRepeat = repeat;
      _this2.initUniformsBuffer();
      const model = yield _this2.layer.buildLayerModel({
        moduleName: 'pointExtrude',
        vertexShader: pointExtrudeVert,
        fragmentShader: pointExtrudeFrag,
        triangulation: PointExtrudeTriangulation,
        defines: _this2.getDefines(),
        inject: _this2.getInject(),
        cull: {
          enable: true,
          face: gl.FRONT
        },
        depth: {
          enable: depth
        }
      });
      return [model];
    })();
  }
  registerBuiltinAttributes() {
    // point layer size;
    this.styleAttributeService.registerStyleAttribute({
      name: 'size',
      type: AttributeType.Attribute,
      descriptor: {
        name: 'a_Size',
        shaderLocation: this.attributeLocation.SIZE,
        buffer: {
          usage: gl.DYNAMIC_DRAW,
          data: [],
          type: gl.FLOAT
        },
        size: 3,
        update: feature => {
          const {
            size
          } = feature;
          if (size) {
            let buffersize = [];
            if (Array.isArray(size)) {
              buffersize = size.length === 2 ? [size[0], size[0], size[1]] : size;
            }
            if (!Array.isArray(size)) {
              buffersize = [size, size, size];
            }
            return buffersize;
          } else {
            return [2, 2, 2];
          }
        }
      }
    });
    this.styleAttributeService.registerStyleAttribute({
      name: 'normal',
      type: AttributeType.Attribute,
      descriptor: {
        name: 'a_Normal',
        shaderLocation: this.attributeLocation.NORMAL,
        buffer: {
          // give the WebGL driver a hint that this buffer may change
          usage: gl.STATIC_DRAW,
          data: [],
          type: gl.FLOAT
        },
        size: 3,
        update: (feature, featureIdx, vertex, attributeIdx, normal) => {
          return normal;
        }
      }
    });
    this.styleAttributeService.registerStyleAttribute({
      name: 'extrude',
      type: AttributeType.Attribute,
      descriptor: {
        name: 'a_Extrude',
        shaderLocation: this.attributeLocation.EXTRUDE,
        buffer: {
          // give the WebGL driver a hint that this buffer may change
          usage: gl.DYNAMIC_DRAW,
          data: [],
          type: gl.FLOAT
        },
        size: 4,
        update: feature => {
          const coordinates = calculateCentroid(feature.coordinates);
          // [lng, lat, lowLng, lowLat]
          // low part for enabled double precision
          return [coordinates[0], coordinates[1], fp64LowPart(coordinates[0]), fp64LowPart(coordinates[1])];
        }
      }
    });
  }
}